import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import { format } from "date-fns";
import toast from 'react-hot-toast';
import { Group, Panel } from 'react-resizable-panels';
import {
    useTheme,
    useMediaQuery,
    CircularProgress,
    Box,
    Typography,
} from "@mui/material";
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { removeItemByValueImmutable } from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FilterButton from 'components/FilterButton';
import FlightFilterDialog from 'components/FlightFilterDialog';
import SearchInput from 'components/SearchInput';
import ResizeSeparator from 'components/ResizeSeparator';
import MapComponent from "./MapComponent";
import SectorList from "./SectorList";


const FlightMap = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // null = not yet loaded (drives loading state)
    const [airports, setAirports] = useState(null);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState(null);
    const [selectedSector, setSelectedSector] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const flightsData = useSelector((state) => state.flights);

    const [splitSize, setSplitSize] = useState(76);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

    const debounceTimer = useRef(null);

    const handleLayoutChange = useCallback((obj) => {
        const sizes = Object.values(obj);
        if (sizes.length === 0) return;
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => setSplitSize(sizes[0]), 200);
    }, []);

    // Airports: fetch once on mount — reference data, never changes mid-session
    useEffect(() => {
        let cancelled = false;
        api.get('/airports')
            .then(r => { if (!cancelled) setAirports(r.data || []); })
            .catch(err => {
                if (!cancelled) {
                    toast.error(err.message || 'Failed to load airports!');
                    setError(err);
                    setAirports([]);
                }
            });
        return () => { cancelled = true; };
    }, []);

    // O(1) airport lookup — rebuilt only when airports list changes (once)
    const airportMap = useMemo(() => {
        if (!airports) return null;
        const map = new Map();
        for (const a of airports) map.set(a.iataCode, a);
        return map;
    }, [airports]);

    // Sectors: derived synchronously — no useState, no extra render cycle.
    // timeMode excluded intentionally: coordinates don't change with timezone.
    const sectors = useMemo(() => {
        if (!airportMap) return [];
        const scheduleFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS);
        const sectorMap = new Map();
        for (const f of scheduleFlights) {
            const depAirport = airportMap.get(f.dep);
            const arrAirport = airportMap.get(f.arr);
            if (!depAirport || !arrAirport) continue;
            if (!sectorMap.has(f.sector)) sectorMap.set(f.sector, []);
            sectorMap.get(f.sector).push({
                ...f,
                depCoordinates: [depAirport.latitude, depAirport.longitude],
                arrCoordinates: [arrAirport.latitude, arrAirport.longitude],
            });
        }
        return [...sectorMap.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([sector, flightList]) => ({ sector, count: flightList.length, flights: flightList }));
    }, [airportMap, flightsData]);

    const loading = airports === null;

    const filteredFlights = useMemo(() => {
        if (!filters) return sectors;

        return sectors
            .map(sector => {
                const filtered = sector.flights.filter(flight => {
                    const etdStr = timeMode === 'UTC'
                        ? flight.etd?.substring(0, 5)
                        : flight.etdLocalBase?.substring(0, 5);

                    if (filters.minEtd && (!etdStr || etdStr < filters.minEtd)) return false;
                    if (filters.maxEtd && (!etdStr || etdStr > filters.maxEtd)) return false;

                    return Object.entries(filters).every(([key, value]) => {
                        if (!value) return true;
                        if (key === 'minEtd' || key === 'maxEtd') return true;
                        if (key === 'flightDate') {
                            const flightDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
                            const filteredDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                            return flightDate === filteredDate;
                        }
                        if (key === 'flightNo') return flight[key] === Number(value);
                        if (key === 'sector') return sector.sector?.toLowerCase().includes(value.toLowerCase());
                        if (key === 'acType' || key === 'flightReg') {
                            return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
                        }
                        return flight[key] === value;
                    });
                });
                return { ...sector, flights: filtered, count: filtered.length };
            })
            .filter(s => s.flights.length > 0)
            .sort((a, b) => a.sector.localeCompare(b.sector));
    }, [timeMode, sectors, filters]);

    const searchedFlights = useMemo(() => {
        if (!searchQuery.trim()) return filteredFlights;
        const q = searchQuery.toLowerCase();
        return filteredFlights
            .map(s => ({
                ...s,
                flights: s.flights.filter(f =>
                    f.flightDesc?.toLowerCase().includes(q) ||
                    f.flightNo?.toString().includes(q) ||
                    f.sector?.toLowerCase().includes(q) ||
                    f.acType?.toLowerCase().includes(q) ||
                    f.flightReg?.toLowerCase().includes(q)
                ),
            }))
            .filter(s => s.flights.length > 0);
    }, [filteredFlights, searchQuery]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    const handleFilterApply = (filterValues) => setFilters(filterValues);

    // Derive from sectors — no need to re-process flightsData a second time
    const { acTypeOptions, flightRegOptions, sectorTypeOptions, departureTypeOptions, arrivalTypeOptions } = useMemo(() => {
        const allFlights = sectors.flatMap(s => s.flights);
        if (!allFlights.length) return {
            acTypeOptions: [], flightRegOptions: [],
            sectorTypeOptions: [], departureTypeOptions: [], arrivalTypeOptions: [],
        };
        const sorted = (key) => [...new Set(allFlights.map(f => f[key]).filter(Boolean))].sort();
        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [sectors]);


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header title="Flight Map" subtitle="List of Sectors" />
                <Box display="flex" alignItems="center" gap={1}>
                    <SearchInput
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <FilterButton filters={filters} onClick={handleFilterOpen} />
                </Box>
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box sx={{ position: 'relative', height: LAYOUT_HEIGHT }}>
                    {isMobile ? (
                        <Box display="flex" flexDirection="column" height="100%" gap={2}>
                            <Box sx={{ flex: '0 0 60%', position: 'relative' }}>
                                <MapComponent
                                    flights={searchedFlights}
                                    selectedSector={selectedSector}
                                />
                            </Box>
                            <Box sx={{ flex: '0 0 60%', overflow: 'auto' }}>
                                <SectorList
                                    flightsList={searchedFlights}
                                    selectedSector={selectedSector}
                                    setSelectedSector={setSelectedSector}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Group
                            direction="horizontal"
                            style={{ height: '100%' }}
                            onLayoutChange={handleLayoutChange}
                        >
                            <Panel defaultSize={splitSize} minSize={0}>
                                <MapComponent
                                    flights={searchedFlights}
                                    selectedSector={selectedSector}
                                    resizeTrigger={splitSize}
                                />
                            </Panel>

                            <ResizeSeparator />

                            <Panel defaultSize={100 - splitSize} minSize={0}>
                                <SectorList
                                    flightsList={searchedFlights}
                                    selectedSector={selectedSector}
                                    setSelectedSector={setSelectedSector}
                                />
                            </Panel>
                        </Group>
                    )}
                </Box>
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions}
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default FlightMap;
